import React, { useEffect, useState } from 'react';
import { Carousel } from 'react-bootstrap';

import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { navBarHide, navBarShow } from '../../../../utils/nav.bar.util';
import { fetchDocumentPdf } from '../../../../scripts/document.pdf.script';
import { documentPdfFilename, attachmentPdfFilename } from '../../../../scripts/document.filename.script';

import AppPdfViewer from '../../../../components/element/PdfViewer';

export interface AppPurchaseViewDraftProps {
  record: any,
  vouchers?: {list: boolean, value: {name: string, size: number, type: string, view: string, file: any}[], valid: {name: boolean, file: boolean}[]},
  showGeneratedPdf?: boolean,
  companyId?: string,
  pdfPath?: string,
  documentId?: string,
  asideMinimize?: string,
  setAsideMinimize?: (value: string) => void
};

interface Slide {
  url: string,
  label: string,
  downloadName: string,
  isGenerated: boolean
}

const emptyVouchers = {list: true, value: [] as any[], valid: [] as any[]}

const AppPurchaseViewDraft: React.FunctionComponent<AppPurchaseViewDraftProps> = ({record, vouchers = emptyVouchers, showGeneratedPdf, companyId, pdfPath, documentId, asideMinimize: asideMinimizeProp, setAsideMinimize: setAsideMinimizeProp}) => {
  const {lang} = StateLanguage()

  const [internalAside, setInternalAside] = useState("off")

  const asideMinimize = asideMinimizeProp ?? internalAside
  const setAsideMinimize = setAsideMinimizeProp ?? setInternalAside

  const [generatedPdfUrl, setGeneratedPdfUrl] = useState<string | null>(null)
  const [generatedPdfLoading, setGeneratedPdfLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const executeShowReference = () => {
    navBarShow('button-navbar-aside-minimize')
    setAsideMinimize("on")
  }

  const executeHideReference = () => {
    navBarHide('button-navbar-aside-minimize')
    setAsideMinimize("off")
  }

  useEffect(() => {
    if (showGeneratedPdf && companyId && pdfPath && documentId && !generatedPdfUrl && !generatedPdfLoading) {
      setGeneratedPdfLoading(true)
      fetchDocumentPdf(companyId, pdfPath, documentId)
        .then((blobUrl) => {
          setGeneratedPdfUrl(blobUrl)
        })
        .catch(() => {
          setGeneratedPdfUrl(null)
        })
        .finally(() => {
          setGeneratedPdfLoading(false)
        })
    }

    return () => {
      if (generatedPdfUrl) {
        URL.revokeObjectURL(generatedPdfUrl)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGeneratedPdf, companyId, pdfPath, documentId])

  const typeLabelFromPath = (path?: string): string => {
    if (!path) return lang.labels.document ?? 'document'
    if (path.includes('invoice')) return lang.labels.invoice ?? lang.labels.providerInvoice ?? 'invoice'
    if (path.includes('quote')) return lang.labels.quote ?? 'quote'
    if (path.includes('delivery-note')) return lang.labels.deliveryNote ?? 'delivery-note'
    if (path.includes('order')) return lang.labels.order ?? lang.labels.providerOrder ?? 'order'
    if (path.includes('pos/sales')) return lang.labels.sale ?? 'sale'
    if (path.includes('expense')) return lang.labels.expense ?? 'expense'
    return lang.labels.document ?? 'document'
  }

  const slides: Slide[] = []

  if (showGeneratedPdf && generatedPdfUrl) {
    const genFilename = documentPdfFilename(record, lang.labels.draft, typeLabelFromPath(pdfPath))
    slides.push({ url: generatedPdfUrl, label: lang.labels.documentPdf, downloadName: genFilename, isGenerated: true })
  }

  for (const item of vouchers.value) {
    if (item.view) {
      slides.push({ url: item.view, label: item.name || lang.labels.referencePdf, downloadName: attachmentPdfFilename(item.name, lang.labels.referencePdf), isGenerated: false })
    }
  }

  const totalSlides = slides.length
  const hasContent = record && (totalSlides > 0 || (showGeneratedPdf && generatedPdfLoading))

  return (
    <>
      { hasContent &&
        <>
          <div id="container-viewer-pdf" className={`flex-column flex-lg-row-auto order-1 order-lg-2 mb-5 mb-lg-0 ${asideMinimize === "off" && "d-none"}`}>
            <div className="card card-flush">
              <div className="card-header pt-3 flex-nowrap">
                <div className="card-title">
                  <h4>{lang.labels.referencePdf}</h4>
                  { totalSlides > 1 &&
                    <span className="badge badge-light fs-7 ms-3">{activeIndex + 1} / {totalSlides}</span>
                  }
                </div>
                <div className="card-toolbar d-none d-lg-flex">
                  { slides[activeIndex] &&
                    <button className="btn btn-sm btn-icon explore-btn-dismiss me-1" type="button" title={lang.labels.download} onClick={() => {
                      const slide = slides[activeIndex]
                      if (!slide?.url) return
                      const link = document.createElement('a')
                      link.href = slide.url
                      link.download = slide.downloadName
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }}>
                      <i className="bi bi-download fs-4"></i>
                    </button>
                  }
                  <button className="btn btn-sm btn-icon explore-btn-dismiss" type="button" onClick={executeHideReference}>
                    <span className="svg-icon svg-icon-lg-2x">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                        <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
              { generatedPdfLoading &&
                <div className="card-body pt-0 text-center">
                  <span className="spinner-border spinner-border-sm align-middle me-2"></span>
                  {lang.labels.loading}
                </div>
              }
              { !generatedPdfLoading && totalSlides > 1 &&
                <Carousel fade variant="dark" interval={null} wrap={false} activeIndex={activeIndex} onSelect={(index: number) => setActiveIndex(index)}>
                  { slides.map (( (slide, index) => { return (
                    <Carousel.Item key={index}>
                      <div className="card-body pt-0">
                        <div className="text-muted fs-8 fw-bold mb-2">{slide.label}</div>
                        <AppPdfViewer pdf={slide.url}></AppPdfViewer>
                      </div>
                    </Carousel.Item>
                  )}))}
                </Carousel>
              }
              { !generatedPdfLoading && totalSlides === 1 &&
                <div className="card-body pt-0">
                  <div className="text-muted fs-8 fw-bold mb-2">{slides[0].label}</div>
                  <AppPdfViewer pdf={slides[0].url}></AppPdfViewer>
                </div>
              }
            </div>
          </div>
          <button className={`explore-toggle btn btn-primary shadow-lg position-fixed transform-90 rounded-top-0 fw-bolder fs-5 px-10 top-50 end-0 ${asideMinimize === "on" && "d-none"}`} type="button" style={{zIndex: 10}} onClick={executeShowReference}>
            <span>{lang.labels.viewReferencePdf}</span>
          </button>
        </>
      }
    </>
  )
};

export default AppPurchaseViewDraft;
